import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'

// ── RewatchStart ─────────────────────────────────────────────────────────────
// Starting a rewatch used to just close RewatchSheet silently (wl.setStatus then
// onClose, no acknowledgement). This mints a short "Rewatch #N" cinematic beat —
// a spin, then a stamp — using the REAL show + rewatch count useWatchlist.setStatus
// dispatches on `nextep:rewatchstart` (src/hooks/useWatchlist.jsx), not placeholder
// data. Mounted once in CinematicLayer.jsx alongside the app's other one-shot
// cinematics; short enough (~2.4s) that it doesn't need the __nextepWrapUntil hold
// FinalCut/CanonSeal use for their much longer sequences.
export function RewatchStart() {
  const [data, setData]   = useState(null)   // { show, count }
  const [stage, setStage] = useState('spin') // 'spin' | 'stamp' | 'out'
  const timers = useRef([])
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)) }
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail
      if (!detail?.show?.id) return
      clear()
      setData(detail)
      setStage('spin')
      if (reduce) {
        // Reduced motion: skip straight to the stamp, hold briefly, done.
        later(() => setStage('stamp'), 30)
        later(() => setStage('out'), 1700)
        later(() => setData(null), 2000)
      } else {
        later(() => setStage('stamp'), 1050)
        later(() => setStage('out'), 2350)
        later(() => setData(null), 2750)
      }
    }
    window.addEventListener('nextep:rewatchstart', handler)
    return () => { window.removeEventListener('nextep:rewatchstart', handler); clear() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dismiss = () => {
    clear()
    setStage('out')
    later(() => setData(null), 300)
  }

  if (!data) return null
  const { show, count } = data
  const posterUrl = show.poster_path ? `https://image.tmdb.org/t/p/w342${show.poster_path}` : null

  return createPortal((
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
      background: 'var(--bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)',
      opacity: stage === 'out' ? 0 : 1,
      transition: 'opacity 0.4s ease',
      cursor: 'pointer',
    }}>
      {stage === 'spin' && !reduce && (
        <div style={{ position: 'relative', width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', inset: -16, borderRadius: 'var(--radius-full)',
            background: 'radial-gradient(circle, rgba(162,155,254,0.4), transparent 70%)',
          }} className="nxa-rw-glow" />
          <div style={{ fontSize: 64 }} className="nxa-rw-spin">🔁</div>
        </div>
      )}

      {(stage === 'stamp' || stage === 'out') && (
        <div style={{ textAlign: 'center' }} className={reduce ? undefined : 'nxa-rw-stamp'}>
          {posterUrl && (
            <img src={posterUrl} alt="" draggable="false" style={{
              width: 72, height: 108, objectFit: 'cover', borderRadius: 'var(--radius-sm)',
              margin: '0 auto 16px', display: 'block',
              boxShadow: '0 10px 30px -8px rgba(0,0,0,0.7)',
            }} />
          )}
          <div style={{
            display: 'inline-block', padding: '10px 24px',
            border: '2px solid var(--purple)', borderRadius: 'var(--radius-md)',
            color: 'var(--accent-text)', fontWeight: 800, fontSize: 22, letterSpacing: '0.04em',
            boxShadow: '0 0 30px rgba(162,155,254,0.4)',
          }}>
            🔁 Rewatch #{count}
          </div>
          <div style={{ color: 'var(--text-strong)', fontSize: 15, fontWeight: 700, marginTop: 14 }}>
            {show.name}
          </div>
          <div style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 6 }}>
            Round two begins. Enjoy it all over again.
          </div>
        </div>
      )}

      <style>{`
        @keyframes rwSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes rwGlowPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @keyframes rwStamp {
          0%   { opacity: 0; transform: scale(2.2) rotate(-18deg); }
          55%  { opacity: 1; transform: scale(0.92) rotate(4deg); }
          75%  { transform: scale(1.06) rotate(-2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  ), document.body)
}
